import { useMemo } from 'react';
import { Link, useParams } from 'react-router';

import type {
  ContractResponse,
  CoveragePeriodResponse,
  InsuredPersonLookupResponse,
  LeavePeriodResponse,
  LookupContractEntry,
  PersonHistoryResponse,
  SalaryVersionResponse,
} from '../../api';
import {
  AsyncSection,
  DataTable,
  EmptyState,
  PageHeader,
  Panel,
  type AsyncState,
  type Column,
} from '../../components';
import {
  EMPTY_VALUE,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatDecimal,
} from '../../format';
import { staffCompanyPath } from '../../routes/paths';
import {
  isRefusal,
  useCompanyContracts,
  useStaffPersonHistories,
  useStaffPersonLookup,
} from '../queries';
import { PERSON_COPY, personContractLabel, personDescription } from './segurados';

/**
 * One segurado of one company, under every contract they are under, with the
 * dated record of each (#418).
 *
 * The staff counterpart of the portal's `PersonHistoryPage`, for the phone call
 * "when did this change?": an analyst reads the same dated record the customer
 * sees, without signing in as them. A person under vida and saúde is two
 * `insured_person` rows; this is one page with a block per row, and opening it
 * by either row's id shows the same blocks.
 *
 * The address carries two opaque uuids and nothing else, and no request the
 * page makes carries the CPF anywhere but in the body of the lookup -- see
 * `useStaffPersonLookup`. Every request carries the company in the URL as
 * `X-Company-Id`.
 *
 * An id no contract of the company has -- invented, stale, or another
 * company's -- is a `404` from the API, and renders as "not here" with the way
 * back, not as a failure with a retry that would never work. A contract the
 * person is not under is simply not a block.
 *
 * Read-only: nothing here writes roster data.
 */

const COVERAGE_COLUMNS: readonly Column<CoveragePeriodResponse>[] = [
  {
    key: 'effective_from',
    header: PERSON_COPY.startHeader,
    align: 'end',
    render: (period) => formatDate(period.effective_from),
  },
  {
    key: 'effective_to',
    header: PERSON_COPY.endHeader,
    align: 'end',
    render: (period) => formatDate(period.effective_to),
  },
  {
    key: 'created_at',
    header: PERSON_COPY.recordedAtHeader,
    align: 'end',
    render: (period) => formatDateTime(period.created_at),
  },
];

const SALARY_COLUMNS: readonly Column<SalaryVersionResponse>[] = [
  {
    key: 'effective_from',
    header: PERSON_COPY.fromHeader,
    align: 'end',
    render: (version) => formatDate(version.effective_from),
  },
  {
    key: 'salary',
    header: PERSON_COPY.salaryHeader,
    align: 'end',
    render: (version) => formatCurrency(version.salary),
  },
  {
    key: 'created_at',
    header: PERSON_COPY.recordedAtHeader,
    align: 'end',
    render: (version) => formatDateTime(version.created_at),
  },
];

const LEAVE_COLUMNS: readonly Column<LeavePeriodResponse>[] = [
  {
    key: 'effective_from',
    header: PERSON_COPY.startHeader,
    align: 'end',
    render: (period) => formatDate(period.effective_from),
  },
  {
    key: 'effective_to',
    header: PERSON_COPY.endHeader,
    align: 'end',
    // An open leave has no return date yet, which is a fact about the leave and
    // not a missing value.
    render: (period) =>
      period.effective_to === null || period.effective_to === undefined
        ? PERSON_COPY.openReturn
        : formatDate(period.effective_to),
  },
  {
    key: 'created_at',
    header: PERSON_COPY.recordedAtHeader,
    align: 'end',
    render: (period) => formatDateTime(period.created_at),
  },
];

function BackToSegurados({ companyId }: { companyId: string }) {
  return <Link to={staffCompanyPath(companyId, 'segurados')}>{PERSON_COPY.backToSegurados}</Link>;
}

export function StaffPersonPage() {
  const { empresaId = '', pessoaId = '' } = useParams();
  const lookup = useStaffPersonLookup(empresaId, pessoaId);

  if (lookup.isNotFound) {
    return (
      <div className="page">
        <PageHeader title={PERSON_COPY.title} />
        <EmptyState
          title={PERSON_COPY.notFound}
          description={PERSON_COPY.notFoundHint}
          action={<BackToSegurados companyId={empresaId} />}
        />
      </div>
    );
  }

  return (
    <div className="page">
      <AsyncSection
        state={lookup}
        loadingLabel={PERSON_COPY.loading}
        errorTitle={PERSON_COPY.error}
      >
        {(person) => <PersonRecord companyId={empresaId} person={person} />}
      </AsyncSection>
    </div>
  );
}

function PersonRecord({
  companyId,
  person,
}: {
  companyId: string;
  person: InsuredPersonLookupResponse;
}) {
  const contracts = useCompanyContracts(companyId);
  const histories = useStaffPersonHistories(companyId, person.contracts);
  const contractsById = useMemo(
    () => new Map((contracts.data?.items ?? []).map((contract) => [contract.id, contract])),
    [contracts.data],
  );

  return (
    <>
      <PageHeader
        title={person.full_name}
        description={personDescription(person.cpf, person.birth_date)}
        actions={<BackToSegurados companyId={companyId} />}
      />
      {person.contracts.map((entry, index) => {
        const history = histories[index];
        // A row the lookup named and the history then refuses is not this
        // person's on this company any more: not a block, and not an error.
        if (history === undefined || isRefusal(history.error)) {
          return null;
        }
        return (
          <ContractBlock
            key={entry.insured_person_id}
            entry={entry}
            contract={contractsById.get(entry.contract_id)}
            history={history}
          />
        );
      })}
    </>
  );
}

function ContractBlock({
  entry,
  contract,
  history,
}: {
  entry: LookupContractEntry;
  contract: ContractResponse | undefined;
  history: AsyncState<PersonHistoryResponse>;
}) {
  return (
    <Panel
      title={personContractLabel(entry, contract)}
      description={entry.covered_today ? PERSON_COPY.coveredToday : PERSON_COPY.notCoveredToday}
    >
      <AsyncSection
        state={history}
        loadingLabel={PERSON_COPY.historyLoading}
        errorTitle={PERSON_COPY.historyError}
      >
        {(data) => (
          <>
            <h3 className="panel__subheading">{PERSON_COPY.insurerTitle}</h3>
            <dl className="definitions">
              <div>
                <dt>{PERSON_COPY.certificateLabel}</dt>
                <dd>{data.person.certificate_number ?? EMPTY_VALUE}</dd>
              </div>
              <div>
                <dt>{PERSON_COPY.insuredCapitalLabel}</dt>
                <dd>{formatDecimal(data.person.insured_capital)}</dd>
              </div>
              <div>
                <dt>{PERSON_COPY.premiumLabel}</dt>
                <dd>{formatDecimal(data.person.premium)}</dd>
              </div>
            </dl>

            <h3 className="panel__subheading">{PERSON_COPY.coverageTitle}</h3>
            <DataTable
              caption={PERSON_COPY.coverageCaption}
              columnVisibility={false}
              columns={COVERAGE_COLUMNS}
              rows={data.coverage_periods}
              rowKey={(period) => period.id}
              emptyTitle={PERSON_COPY.coverageEmpty}
            />

            <h3 className="panel__subheading">{PERSON_COPY.salaryTitle}</h3>
            <DataTable
              caption={PERSON_COPY.salaryCaption}
              columnVisibility={false}
              columns={SALARY_COLUMNS}
              rows={data.salary_versions}
              rowKey={(version) => version.id}
              emptyTitle={PERSON_COPY.salaryEmpty}
            />

            <h3 className="panel__subheading">{PERSON_COPY.leaveTitle}</h3>
            <DataTable
              caption={PERSON_COPY.leaveCaption}
              columnVisibility={false}
              columns={LEAVE_COLUMNS}
              rows={data.leave_periods ?? []}
              rowKey={(period) => period.id}
              emptyTitle={PERSON_COPY.leaveEmpty}
            />
          </>
        )}
      </AsyncSection>
    </Panel>
  );
}
